import React from 'react';
import {User, UserData} from "types/User";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import Toggle from "components/Form/Toggle";
import Uploader from "../../../components/Form/Uploader";
import {PhotographIcon} from "@heroicons/react/outline";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";

type AudienceMemberFormProps = {
  audienceMember?: User | null
  onSubmit: (values: UserData, helpers: FormikHelpers<UserData>) => any
}

export default function AudienceMemberForm(props: AudienceMemberFormProps) {

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("This field is required")
      .min(2, "Minimum of 2 characters")
      .max(74, "Maximum of 74 characters"),
    email: Yup.string()
      .email('Must be a valid email address')
      .required('This field is required')
      .max(255, "Maximum of 255 characters"),
  })

  const initialValues: UserData = {
    name: props.audienceMember?.name || '',
    email: props.audienceMember?.email || '',
    profile_picture_id: props.audienceMember?.profile_picture?.id || null,
    is_claimed: props.audienceMember?.is_claimed || false,
    is_verified: props.audienceMember?.email_is_verified || false,
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => (
        <Form className="space-y-8 divide-y divide-gray-200">
          <ErrorNotification formik={formik} />

          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Account Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Enter the basic account details for this audience member.
                    </p>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                    <div className="sm:col-span-3 sm:col-start-1">
                      <div className="mt-1">
                        <Field
                          label='Name *'
                          hint='74 max chars'
                          type="text"
                          name="name"
                          id="name"
                          maxLength="74"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-3">
                      <div className="mt-1">
                        <Field
                          label='Email *'
                          hint='255 chars max'
                          type="email"
                          name="email"
                          id="email"
                          maxLength="255"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <Toggle
                        label="Claimed"
                        description="Whether this person has claimed their account"
                        checked={formik.values.is_claimed}
                        onChange={checked => formik.setFieldValue('is_claimed', checked)}
                      />
                    </div>

                    <div className="sm:col-span-6">
                      <Toggle
                        label="Verified"
                        description="Whether this person has verified their email address"
                        checked={formik.values.is_verified}
                        onChange={checked => formik.setFieldValue('is_verified', checked)}
                      />
                    </div>
                  </div>

                  {/* Media */}
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Media
                      </h3>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Files */}
                      <div className="sm:col-span-6">
                        <Uploader
                          id={'profile_picture_id'}
                          name={'profile_picture_id'}
                          icon={PhotographIcon}
                          files={props.audienceMember?.profile_picture}
                          type={Uploader.types.IMAGE}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('profile_picture_id', files[0]?.id ?? null)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <FormControls formik={formik}/>
          </div>
        </Form>
      )}
    </Formik>
  );
}